/* eslint-disable react/prop-types */
import React, { useState, useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import clone from 'lodash/clone';

import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import List from '@mui/material/List';
import ListSubheader from '@mui/material/ListSubheader';
import { FormattedMessage } from 'react-intl';

import Button from '@ts-core/components/Button';

import GroupedStudents from '../../components/GroupedStudents';

import { useMessageActions } from '../../hooks';

function getStudentsByGroup(students) {
  if (!Array.isArray(students)) {
    return new Map();
  }

  const groupedStudents = new Map();
  /* eslint-disable no-unused-vars */
  for (const student of students) {
    if (!student.group) {
      continue;
    }

    let group = groupedStudents.get(student.group._id);
    group = group || [];
    group.push(student);

    groupedStudents.set(student.group._id, group);
  }
  /* eslint-enable no-unused-vars */

  return groupedStudents;
}

const MessagesPage = ({
  courseId,
  students,
  // addStudentsToSet,
  // removeStudentsFromSet,
  // intl: internationalizeMessage,
  // sendMessage,
  // clearState,
  // isValidState,
  // areBeingSent,
  // messagesWereSentSuccessfully,
  // onSendMessageComplete,
  closeEnclosingDialog,
}) => {
  const [selectedStudents, setSelectedStudents] = useState(new Set());
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const intl = useIntl();
  const { onSendMessage, sendMessageLoading } = useMessageActions({ courseId });

  const groupedStudents = useMemo(() => {
    return getStudentsByGroup(students);
  }, [students]);

  const isValidState = useMemo(() => {
    return title && content && selectedStudents && selectedStudents.size > 0;
  }, [title, content, selectedStudents]);

  const handleSendMessage = useCallback(() => {
    const studentsArray = Array.from(selectedStudents);
    onSendMessage(title, content, studentsArray, () => {
      setTitle('');
      setContent('');
      setSelectedStudents(new Set());
      closeEnclosingDialog();
    });
  }, [title, content, selectedStudents, onSendMessage, closeEnclosingDialog]);

  const addStudentToSet = useCallback((studentsToAdd) => {
    setSelectedStudents((state) => {
      for (const student of studentsToAdd) {
        state.add(student._id);
      }
      return clone(state);
    });
  }, []);
  const removeStudentFromSet = useCallback((studentsToRemove) => {
    setSelectedStudents((state) => {
      for (const student of studentsToRemove) {
        state.delete(student._id);
      }
      return clone(state);
    });
  }, []);

  return (
    <Box position="relative" width="100%">
      <TextField
        sx={{
          mt: 1,
          mb: 3,
        }}
        rows="1"
        placeholder={intl.formatMessage({
          id: 'messages.writeTitle',
        })}
        fullWidth
        onChange={(e) => setTitle(e.target.value)}
        value={title}
        variant="outlined"
      />
      <TextField
        multiline
        rows="6"
        placeholder={intl.formatMessage({
          id: 'messages.writeMessage',
        })}
        fullWidth
        onChange={(e) => setContent(e.target.value)}
        value={content}
        variant="outlined"
      />
      <List
        component="nav"
        aria-labelledby="nested-list-subheader"
        subheader={
          <ListSubheader component="div" id="nested-list-subheader">
            <FormattedMessage id="messages.pickRecipient" />
          </ListSubheader>
        }
      >
        <GroupedStudents
          addStudents={addStudentToSet}
          removeStudents={removeStudentFromSet}
          groupedStudents={groupedStudents}
          isSelectedStudent={(student) => selectedStudents.has(student._id)}
        />
      </List>
      <Box display="flex" justifyContent="flex-end">
        <Button variant="contained" color="primary" onClick={handleSendMessage} disabled={!isValidState} loading={sendMessageLoading}>
          <FormattedMessage id="messages.send" />
        </Button>
      </Box>
    </Box>
  );
};

MessagesPage.propTypes = {
  students: PropTypes.array.isRequired,
  courseId: PropTypes.string.isRequired,

  closeEnclosingDialog: PropTypes.func.isRequired,
};

export default MessagesPage;
